import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatCurrency } from '../../utils/api'

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20, light = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${light ? 'rgba(255,255,255,0.3)' : theme.colors.grey200}`,
    borderTopColor: light ? 'white' : theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const Alert = ({ type = 'warning', children }) => {
  const types = {
    warning: { bg: theme.colors.amberLight, border: theme.colors.sunburst, icon: '⚠️' },
    error: { bg: theme.colors.redLight, border: theme.colors.crimson, icon: '✕' }
  }
  const t = types[type]
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: theme.radius.md,
      padding: '12px 16px',
      fontSize: '14px',
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    }}>
      <span>{t.icon}</span>
      <div>{children}</div>
    </div>
  )
}

function Receiving() {
  const navigate = useNavigate()
  const [searchType, setSearchType] = useState('dealId')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [selectedItems, setSelectedItems] = useState([])
  const [step, setStep] = useState('search') // search, results, confirm, success
  const [result, setResult] = useState(null)

  // Navigate to testing with the deal ID
  const handleTestNow = () => {
    const testDealId = result?.action === 'split' ? result.newDealId : result?.dealId
    if (testDealId) {
      navigate(`/warehouse/testing?dealId=${testDealId}`)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    setError(null)
    setSearchResults([])
    try {
      const response = await api.get(`/api/warehouse/receiving/search?q=${encodeURIComponent(searchQuery)}&type=${searchType}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        setError('No deals found in "Shipping Arranged" status.')
      } else if (deals.length === 1) {
        selectDeal(deals[0])
      } else {
        setSearchResults(deals)
        setStep('results')
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const selectDeal = (deal) => {
    setSelectedDeal(deal)
    setSelectedItems([])
    setStep('confirm')
  }

  const toggleItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
  }

  const selectAllItems = () => {
    if (selectedDeal?.items) {
      setSelectedItems(selectedDeal.items.map(i => i.id))
    }
  }

  const reset = () => {
    setSearchQuery('')
    setSearchResults([])
    setSelectedDeal(null)
    setSelectedItems([])
    setError(null)
    setStep('search')
    setResult(null)
  }

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/api/warehouse/receiving/mark-received', {
        dealId: selectedDeal.id,
        receivedItemIds: selectedItems,
        allItems: selectedDeal.items,
        isListingService: selectedDeal.isListingService || false
      })
      setResult(response)
      setStep('success')
    } catch (err) {
      setError(err.message || 'Failed to mark as received')
    } finally {
      setLoading(false)
    }
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>📥</div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Goods Receiving</h2>
            <p style={{ color: theme.colors.grey500 }}>Search for shipments to receive</p>
          </div>

          <div style={{
            display: 'flex', gap: '4px', marginBottom: '24px',
            background: theme.colors.grey100, borderRadius: theme.radius.md, padding: '4px'
          }}>
            {['dealId', 'postcode'].map(t => (
              <button
                key={t}
                onClick={() => setSearchType(t)}
                style={{
                  flex: 1, padding: '10px', border: 'none',
                  borderRadius: theme.radius.sm,
                  background: searchType === t ? theme.colors.white : 'transparent',
                  fontWeight: searchType === t ? 600 : 400,
                  cursor: 'pointer'
                }}
              >
                {t === 'dealId' ? 'Deal ID' : 'Postcode'}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              placeholder={searchType === 'dealId' ? 'Enter Deal ID' : 'Enter Postcode'}
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px',
                fontFamily: searchType === 'dealId' ? theme.fonts.sans : 'inherit',
                textAlign: 'center', outline: 'none'
              }}
            />
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <Button onClick={handleSearch} disabled={loading || !searchQuery.trim()} fullWidth size="lg">
            {loading ? <><Spinner size={18} /> Searching...</> : 'Search'}
          </Button>
        </Card>
      </div>
    )
  }

  // Results Screen (multiple deals found)
  if (step === 'results') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
            {searchResults.length} Deal{searchResults.length !== 1 ? 's' : ''} Found
          </h2>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {searchResults.map(deal => (
          <Card
            key={deal.id}
            onClick={() => selectDeal(deal)}
            style={{ marginBottom: '12px', cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                  {deal.id}
                </div>
                <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {deal.customerName || deal.dealName}
                  {deal.isListingService && <Badge color="purple">Listing Service</Badge>}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                  {deal.items?.length || 0} item{(deal.items?.length || 0) !== 1 ? 's' : ''} • {deal.boxCount || 1} box{(deal.boxCount || 1) !== 1 ? 'es' : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {deal.postcode && <Badge>{deal.postcode}</Badge>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  // Confirm Screen
  if (step === 'confirm' && selectedDeal) {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
              {selectedDeal.id}
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedDeal.customerName || selectedDeal.dealName}
              {selectedDeal.isListingService && <Badge color="purple">Listing Service</Badge>}
            </h2>
          </div>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {/* Deal Info */}
        <Card style={{ marginBottom: '20px', padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Boxes</div>
              <div style={{ fontSize: '24px', fontWeight: 700, fontFamily: theme.fonts.sans }}>{selectedDeal.boxCount || 1}</div>
            </div>
            {selectedDeal.carrier && (
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Carrier</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{selectedDeal.carrier}</div>
              </div>
            )}
            {selectedDeal.trackingNumber && (
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Tracking</div>
                <div style={{ fontSize: '14px', fontFamily: theme.fonts.sans }}>{selectedDeal.trackingNumber}</div>
              </div>
            )}
          </div>
        </Card>

        {/* Items Selection */}
        <Card padding={false} style={{ marginBottom: '20px' }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${theme.colors.grey200}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div style={{ fontWeight: 600 }}>
              Items ({selectedItems.length}/{selectedDeal.items?.length || 0} selected)
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" size="sm" onClick={selectAllItems}>Select All</Button>
              {selectedItems.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedItems([])}>Clear</Button>
              )}
            </div>
          </div>

          <div style={{ padding: '12px' }}>
            {(selectedDeal.items || []).map(item => {
              const isSelected = selectedItems.includes(item.id)
              return (
                <div
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px', marginBottom: '8px',
                    background: isSelected ? theme.colors.greenLight : theme.colors.grey100,
                    border: `2px solid ${isSelected ? theme.colors.greenOnions : 'transparent'}`,
                    borderRadius: theme.radius.md,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: isSelected ? theme.colors.greenOnions : theme.colors.white,
                    border: isSelected ? 'none' : `2px solid ${theme.colors.grey300}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.colors.white, fontSize: '14px', flexShrink: 0
                  }}>
                    {isSelected && '✓'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{item.name || item.productName}</div>
                    {(item.sellPrice || item.buyPrice) && (
                      <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '2px' }}>
                        {item.buyPrice && <span>Buy: {formatCurrency(item.buyPrice)}</span>}
                        {item.buyPrice && item.sellPrice && <span> • </span>}
                        {item.sellPrice && <span>Sell: {formatCurrency(item.sellPrice)}</span>}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>

        {error && <Alert type="error">{error}</Alert>}

        {selectedItems.length > 0 && selectedItems.length < (selectedDeal.items?.length || 0) && (
          <Alert type="warning">
            Partial receipt: A new deal will be created for {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''}.
          </Alert>
        )}

        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={loading || selectedItems.length === 0}
          fullWidth
          size="lg"
        >
          {loading ? <><Spinner size={18} light /> Processing...</> : `Confirm ${selectedItems.length} Item${selectedItems.length !== 1 ? 's' : ''} Received`}
        </Button>
      </div>
    )
  }

  // Success Screen
  if (step === 'success') {
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>Items Received</h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '24px' }}>
            {result?.message || 'Items have been marked as received successfully.'}
          </p>

          {result && (
            <div style={{
              background: theme.colors.grey100,
              borderRadius: theme.radius.md,
              padding: '16px',
              textAlign: 'left',
              marginBottom: '24px'
            }}>
              {result.action === 'split' ? (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>New Deal (Arrived to Test)</div>
                    <div style={{ fontFamily: theme.fonts.sans, fontWeight: 600 }}>{result.newDealId}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Original Deal (Shipping Arranged)</div>
                    <div style={{ fontFamily: theme.fonts.sans, fontWeight: 600 }}>{result.originalDealId}</div>
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Deal moved to Arrived to Test</div>
                  <div style={{ fontFamily: theme.fonts.sans, fontWeight: 600 }}>{result.dealId || selectedDeal?.id}</div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button variant="primary" onClick={handleTestNow} fullWidth size="lg">
              Test Now
            </Button>
            <Button variant="secondary" onClick={reset} fullWidth size="lg">
              Receive Another Shipment
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return null
}

export default Receiving
